import Dropzone from 'dropzone';
import { ConditionalLogic, DropzoneHandler, FetchManager } from 'js-pimcore-formbuilder';
import { CUSTOM_EVENTS_MAP } from '@/setup/globals';
import { TurnstileCaptcha } from '@/pimcore/formbuilder-captcha.js';
import { CaptureQueries } from '@/pimcore/formbuilder-capture-queries.js';


/**
 * This file handles the front end part of the FormBuilder Bundle.
 * It needs to be included and loaded in a custom theme in order to work.
 *
 * @see https://github.com/dachcom-digital/js-pimcore-formbuilder
 */
(function () {
  const EVENTS = {
    domLoaded: 'DOMContentLoaded',
  };

  const SELECTORS = {
    dataWidget: '[data-widget]',
    notificationContainer: '[data-notifications-container]',
    submitButton: '[type="submit"]',
    formSelector: 'form.formbuilder.ajax-form',
  };

  const CLASSES = {
    bemModifierError: '--state-error',
    buttonProgress: 'e-button--progress',
    form: 'c-form',
    formRow: 'c-form__row',
    notificationsList: 'c-form__notifications-list',
    // This class is hard coded within the FormBuilder bundle, in order to make it work it needs to be this class.
    conditionalHiddenClass: 'fb-cl-hide-element',
  };

  const MAX_LOADING_ATTEMPTS = 20;
  const LOADING_ATTEMPT_DELAY = 250; // milli seconds

  // Timer to handle the input focus delay.
  let focusTimeout;
  let loadingAttempts = 0;

  /**
   * Add event listeners to trigger the component initialization.
   */
  document.addEventListener(EVENTS.domLoaded, tryToLoadComponent);
  document.addEventListener(CUSTOM_EVENTS_MAP.formbuilderRendered, tryToLoadComponent);

  /**
   * The form builder can be rendered as an area brick or be passed to vue and initialized after vue has been rendered.
   * As the form builder requires the html to be loaded in the dom and external libraries (like captcha) be loaded,
   * this can lead to some timing issues. This can not be fixed with events as they can miss each other eg when the user has
   * a weak internet connection. The only solution is to have a fixed amount of loading attempts. This fixes 99.99% of all
   * issues. There is still a risk for extremely slow internet connections but this is a known issue and communicated with the
   * customer.
   */
  function tryToLoadComponent() {
    if (document.querySelectorAll(SELECTORS.formSelector).length) {
      initializeComponent();

      return;
    }

    if (loadingAttempts <= MAX_LOADING_ATTEMPTS) {
      setTimeout(() => {
        loadingAttempts++;
        tryToLoadComponent();
      }, LOADING_ATTEMPT_DELAY);
    }
  }

  /**
   * Initialize the component.
   */
  function initializeComponent() {
    const $forms = document.querySelectorAll(SELECTORS.formSelector);

    if (!$forms.length) {
      // eslint-disable-next-line no-console
      console.error('Formbuilder: loading attempt failed, "initializeComponent" requires a form but no forms found.');

      return;
    }

    $forms.forEach(($form) => {
      initializeFormBuilderBundle($form);
    });
  }

  /**
   * Initialize the form builder bundle for a given form.
   *
   * @param {Element} $form - The form DOM element.
   */
  function initializeFormBuilderBundle($form) {
    new FetchManager($form, getFetchManagerConfig($form));
    new DropzoneHandler($form);
    new TurnstileCaptcha($form, {
      captchaFieldClass: 'input.re-captcha-v3',
    });
    new ConditionalLogic($form, {
      conditions: {},
      actions: {
        toggleElement: {
          // This function enables the conditional logic so it should be used to hide elements.
          onEnable(action, actionId, elements) {
            elements.forEach(($element) => toggleRowVisibility($element, 'hide' === action.state));
          },
          // This function disables the conditional logic so it should be used to show elements.
          onDisable(action, actionId, elements) {
            elements.forEach(($element) => toggleRowVisibility($element, 'show' === action.state));
          },
        },
      },
      hideElementClass: CLASSES.conditionalHiddenClass,
    });
    new CaptureQueries($form);
    bindCustomFormEvents($form);
  }

  /**
   * Custom element transformer for FetchManager.
   *
   * @see https://github.com/dachcom-digital/js-pimcore-formbuilder/blob/main/docs/01_fetch_manager.md#custom-element-transformers
   *
   * @param {Element} $form - The form DOM element.
   */
  function getFetchManagerConfig($form) {
    return {
      elementTransformer: {
        addValidationMessage: (form, field, messages) => {
          const $widget = field.dataset.widget ? field : field.closest(getDataWidgetSelector());

          addBemErrorModifier($widget);
          showErrorMessages($widget, messages, $widget.classList.contains(CLASSES.form));
          focusFirstError(form);
          bindFieldEvents(field, $widget);
        },
        removeFormValidations: (form) => {
          removeErrorMessages(form);
          toggleButtonLoadingIndicators(form, false);
        },
      },
      // onRequestDone: (responseData) => {},
      // onFail: (responseData) => {},
      // onError: (responseData) => {},
      // onFatalError: (responseData) => {},
      onGeneralError: (generalErrorMessages) => {
        showErrorMessages($form, generalErrorMessages, true);
      },
      onSuccess: (messages, redirect) => {
        if (redirect) {
          window.location.assign(redirect);

          return;
        }

        showVueNotification(messages);
      },
    };
  }

  /**
   * Triggers a custom event to display messages as vue notifications.
   *
   * @param {Array<String>|Array<Object>} messages - A list of error messages.
   * @param {String} messages.type - The type of the message.
   * @param {String} messages.message - An message text.
   */
  function showVueNotification(messages) {
    if (!messages?.length) {
      return;
    }

    messages.forEach((message) => {
      window.dispatchEvent(new CustomEvent(CUSTOM_EVENTS_MAP.notificationShow, { detail: message }));
    });
  }

  /**
   * Displays all error messages for each required DOM element and sets the BEM error modifiers.
   *
   * @param {Element} $element - The DOM element in which the error messages should be placed.
   * @param {Array<String>|Array<Object>} messages - A list of error messages.
   * @param {String} messages.message - An error message.
   * @param {Boolean} [addToFirstContainer] - Define if the errors should be added to the first container within the element.
   */
  function showErrorMessages($element, messages, addToFirstContainer = false) {
    const $containers = findAllNotificationContainersInside($element);
    // Add errors to the last container by default.
    const containerIndex = addToFirstContainer ? 0 : $containers.length - 1;
    const $notificationContainer = $containers[containerIndex];
    const $errorList = document.createElement('ul');

    $errorList.className = CLASSES.notificationsList;

    // Create a new list item for each message.
    messages.forEach((message) => {
      const messageHtml = document.createElement('li');

      messageHtml.innerHTML = message.message || message;

      $errorList.append(messageHtml);
    });

    $notificationContainer.classList.add('error');
    $notificationContainer.append($errorList);
  }

  /**
   * Removes all form error messages and BEM error modifiers.
   *
   * @param {Element} $element - A DOM element in which all errors will get removed.
   */
  function removeErrorMessages($element) {
    // Remove all error messages.
    findAllNotificationContainersInside($element).forEach((notification) => {
      const childNodes = Array.prototype.slice.call(notification.childNodes);

      childNodes.forEach(function (child) {
        child.remove();
      });
    });

    // Remove the BEM error modifiers.
    if ($element.dataset.widget) {
      removeBemErrorModifier($element);
    } else {
      $element.querySelectorAll(getDataWidgetSelector()).forEach((element) => { removeBemErrorModifier(element); });
    }
  }

  /**
   * Tries to focus the first error message within the form.
   *
   * @param {Element} $element - The form or parent DOM element in which an error should be searched.
   */
  function focusFirstError($element) {
    clearTimeout(focusTimeout);

    focusTimeout = setTimeout(() => {
      const error = $element.querySelector(`.${CLASSES.notificationsList}`);

      if (error) {
        const positionY = error.getBoundingClientRect().top + window.pageYOffset;
        const headerHeight = document.body.style.getPropertyValue('--header-height') || 200;

        window.scrollTo({
          top: positionY - Number.parseInt(headerHeight, 10) - 50,
          behavior: 'smooth',
        });
      }
    }, 100);
  }

  /**
   * Binds all required custom events to the form DOM element.
   *
   * @param {Element} $form - The form DOM element.
   */
  function bindCustomFormEvents($form) {
    // Enable progress animation on submit.
    $form.addEventListener('submit', () => {
      toggleButtonLoadingIndicators($form, true);
    });
  }

  /**
   * Binds all required events to the single form field element.
   *
   * @param {Element} $inputField - An input DOM element.
   * @param {Element} $widget - The parent DOM element which holds the input and the error messages container.
   */
  function bindFieldEvents($inputField, $widget) {
    // Bind events to the field in order to remove the errors after some user interaction.
    $inputField.addEventListener('change', () => removeErrorMessages($widget), { once: true });
    $inputField.addEventListener('input', () => removeErrorMessages($widget), { once: true });
  }

  /**
   * Adds or removes the loading class from the submit buttons.
   *
   * @param {Element} $form - The form DOM element.
   * @param {Boolean} [showLoading] - If the loading indicator should be shown or not.
   */
  function toggleButtonLoadingIndicators($form, showLoading = true) {
    $form.querySelectorAll(SELECTORS.submitButton).forEach(($button) => {
      if (showLoading) {
        $button.classList.add(CLASSES.buttonProgress);
      } else {
        $button.classList.remove(CLASSES.buttonProgress);
      }
    });
  }

  /**
   * Removes a BEM error modifier from a given DOM element.
   *
   * @param {Element} $element - A DOM element.
   */
  function removeBemErrorModifier($element) {
    if (!$element) {
      return;
    }

    const bemBlock = getBemBlock($element);

    $element.classList.remove(bemBlock + CLASSES.bemModifierError);
  }

  /**
   * Adds a BEM error modifier to a given DOM element.
   *
   * @param {Element} $element - A DOM element.
   */
  function addBemErrorModifier($element) {
    if (!$element) {
      return;
    }

    const bemBlock = getBemBlock($element);

    $element.classList.add(bemBlock + CLASSES.bemModifierError);
  }

  /**
   * Returns the BEM class name of a given DOM element.
   *
   * @param {Element} $element - A DOM element.
   *
   * @return {String}
   */
  function getBemBlock($element) {
    return $element.dataset.widget || CLASSES.formRow;
  }

  /**
   * Looks for notification containers within a given DOM element.
   *
   * @param {Element} $element - A DOM element.
   *
   * @return {NodeListOf<Element>}
   */
  function findAllNotificationContainersInside($element) {
    return $element ? $element.querySelectorAll(SELECTORS.notificationContainer) : [];
  }

  /**
   * Returns a selector string to find the correct data widget within a form row.
   *
   * @return {String}
   */
  function getDataWidgetSelector() {
    return `${SELECTORS.dataWidget}, .${CLASSES.formRow}, .${CLASSES.form}`;
  }

  /**
   * Toggles the hidden class for the closest row of a given element.
   *
   * @param {Element} $element - An element within a row container.
   * @param {Boolean} isHidden - Defines if the row should be hidden or visible.
   */
  function toggleRowVisibility($element, isHidden) {
    const rowSelector = `.${CLASSES.formRow}`;

    $element.closest(rowSelector).classList.toggle(CLASSES.conditionalHiddenClass, isHidden);

    if (isHidden) {
      if (['radio', 'checkbox'].includes($element.type)) {
        $element.checked = false;
      } else if ($element.type.includes('select')) {
        // Selects are vue components - this is required as ivoclar has custom selects, and they require to be custom.
        // Therefor we need to fire an event the let vue know that the internal value should be set back to default.
        const resetEvent = new CustomEvent(CUSTOM_EVENTS_MAP.formbuilderHideField, {
          detail: {
            fieldId: $element.id, // Pass the ID of the field
            fieldName: $element.name, // Pass the name of the field
            fieldType: $element.tagName.toLowerCase(), // e.g., 'input', 'select', 'textarea'
          },
        });

        $element.dispatchEvent(resetEvent);
      } else if ($element.type === 'hidden') {
        // There is no other way to detect file upload fields other than this.
        // For file uploads we need to trigger the remove process of Dropzone.
        const parent = $element.parentNode;

        if (!parent.classList.contains('dynamic-multi-file')) {
          return;
        }

        let dropzone = null;

        try {
          dropzone = Dropzone.forElement(parent.querySelector('.dropzone-container'));
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        }

        if (dropzone !== null) {
          dropzone.removeAllFiles(true);
        }
      } else {
        $element.value = null;
      }
    }
  }
})();
